import PropTypes from 'prop-types';
import { StatisticsLayout } from 'views/components';
import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';
import {
    BarChart,
    Card,
    Container,
    DownloadButton,
    InfoCard
} from 'mol-ui';
import {
    Col,
    Row
} from 'reactstrap';
import React, { useCallback, useMemo, useState } from 'react';
import {
    clearObject,
    isLocked,
    objectToChart
} from 'shared/utils';
import {
    parsePsychosocialRiskResult,
    statisticsActions
} from 'core/Statistics';
import {
    useDispatch,
    useSelector
} from 'hooks';

const PsychosocialRisk  = ({
    tabController
}) => {

    const { t } = useTranslation();
    const {
        loading,
        psychosocialRisk,
        psychosocialRiskDownload,
        psychosocialRiskDownloading
    } = useSelector('statistics');
    const { user } = useSelector('account');
    const dispatch = useDispatch();
    const [, setCurrentFilters] = useState({});
    
    const extractDownloadHeaders = (structure, key = 'question_uuid', label = 'content') => {
        if (isEmpty(structure)) return [];
        return structure.map(item => ({
            key: item[key],
            label: item[label]
        }));
    };

    const DEFAULT_HEADERS = [{
        key: 'created_at',
        label: t('Fecha')
    }, {
        key: 'patient_identification',
        label: t('DNIPaciente')
    }, {
        key: 'patient_last_name',
        label: t('Apellido')
    }, {
        key: 'patient_first_name',
        label: t('Nombre')
    }];

    const downloadHeaders = useMemo(() => [...DEFAULT_HEADERS, ...(extractDownloadHeaders(psychosocialRiskDownload?.structure))], [psychosocialRiskDownload]);

    const downloadController = {
        data: psychosocialRiskDownload?.data,
        downloading: psychosocialRiskDownloading,
        fileName: t('Cuestionario de Riesgo Psicosocial'),
        headers: downloadHeaders,
        onDownload: filters => dispatch(statisticsActions.getStatisticsPsychosocialRiskDownload(filters))
    };

    const onFiltersChange = filters => {
        setCurrentFilters(filters);
        dispatch(statisticsActions.getStatisticsPsychosocialRisk(clearObject({
            ...filters
        })));
    };

    const {
        answers,
        results,
        structure,
        survey_completed,
        survey_invited
    } = psychosocialRisk;

    const parsedAnswers = useMemo(() => !answers
        ? []
        : answers.map(a => ({ ...a, ...Object.fromEntries(a?.results?.map(({ uuid, value }) => [uuid, parsePsychosocialRiskResult(value)])) }))
    , [answers]);
    const processedDownloadHeaders = useMemo(() => [...DEFAULT_HEADERS, ...(extractDownloadHeaders(structure))], [structure]);

    const customObjectToChart = useCallback(data => objectToChart(data, parsePsychosocialRiskResult).sort((a, b) => {
        const order = ['none', 'low', 'medium', 'high', 'very_high'];
        return order.indexOf(a.key) - order.indexOf(b.key);
    }), []);

    return (
        <StatisticsLayout
            downloadController={ !isLocked(user, 'download_reports') ? downloadController : undefined }
            filterPatients={ ['psychosocial_risk', 'workplace'] }
            filterProfessionals={ false }
            filterSpecialties={ false }
            loading={ loading }
            tabController={ tabController }
            onFiltersChange={ onFiltersChange }
        >
            <Row>
                <Col md="6" xs="12">
                    <InfoCard
                        caption={ t('Cantidad de cuestionarios completados') }
                        icon="done"
                        title={ (survey_completed ?? 0).toString() }
                    />
                </Col>
                <Col md="6" xs="12">
                    <InfoCard
                        caption={ t('Cantidad de cuestionarios requeridos') }
                        icon="summarize"
                        title={ (survey_invited ?? 0).toString() }
                    />
                </Col>
                {
                    !!survey_completed &&
                    <>
                        <Col xs="12">
                            <DownloadButton
                                caption={ t('Descargar cuestionarios procesados') }
                                data={ parsedAnswers }
                                fileName={ t('Cuestionarios procesados') }
                                fontSize="12px"
                                headers={ processedDownloadHeaders }
                                margin="0px 0px 10px 0px"
                                separator=";"
                                size={ 20 }
                                target="_blank"
                            />
                        </Col>
                        {
                            results.map(({
                                key,
                                value
                            }) => (
                                <Col key={ key } sm="6" xs="12">
                                    <Card
                                        overflowX="auto"
                                        title={ key }
                                    >
                                        <Container minWidth="500px">
                                            <BarChart
                                                config={ [{
                                                    dataKey: 'value',
                                                    fontSize: '11px',
                                                    label: key
                                                }] }
                                                data={ customObjectToChart(value) }
                                                height={ 300 }
                                                labelMaxLength={ 12 }
                                            />
                                        </Container>
                                    </Card>
                                </Col>
                            ))
                        }
                    </>
                }
            </Row>
        </StatisticsLayout>
    );
};

PsychosocialRisk.propTypes = {
    tabController: PropTypes.object
};

export default PsychosocialRisk;
